import React, { useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

function HeaderWithMenu(props) {
  const menuBtnRef = useRef(null);
  const {
    displayName,
    enableSorting,
    progressSort,
    setSort,
    column,
    enableMenu,
    showColumnMenu,
    showColumnMenuAfterButtonClick,
    showColumnMenuAfterMouseClick,
  } = props;

  const sort = column?.getSort?.();

  const openMenu = (e) => {
    e.stopPropagation();
    if (showColumnMenuAfterButtonClick) {
      showColumnMenuAfterButtonClick(menuBtnRef.current);
      return;
    }
    if (showColumnMenuAfterMouseClick) {
      showColumnMenuAfterMouseClick(e);
      return;
    }
    if (showColumnMenu) {
      showColumnMenu(menuBtnRef.current);
    }
  };

  const onTitleClick = () => {
    if (!enableSorting) return;
    if (progressSort) {
      progressSort(false);
      return;
    }
    const next = sort === 'asc' ? 'desc' : sort === 'desc' ? null : 'asc';
    setSort?.(next, false);
  };

  return (
    <div className="commonAgHeaderRoot">
      <span className="commonAgHeaderTitle" onClick={onTitleClick} title={displayName}>
        {displayName}
      </span>
      {enableMenu && (
        <button
          ref={menuBtnRef}
          type="button"
          className="commonAgHeaderMenuBtn"
          onClick={openMenu}
          aria-label={`Open column menu for ${displayName}`}
        >
          ⋮
        </button>
      )}
    </div>
  );
}

/**
 * Shared AG Grid wrapper (Quartz theme). Register modules once via {@link AgGridProvider} in `index.js`.
 *
 * Optional server pagination: pass `pagination`, `paginationModel`, `rowCount`, `onPaginationModelChange`.
 */
export default function CommonAgGrid({
  rowData,
  columnDefs,
  loading = false,
  height = 380,
  className = 'ag-theme-quartz',
  defaultColDef: defaultColDefProp,
  getRowId,
  context,
  pagination = false,
  paginationMode = 'client',
  paginationModel,
  rowCount = 0,
  onPaginationModelChange,
  pageSizeOptions = [5, 10, 25],
  ...gridProps
}) {
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 80,
      suppressHeaderMenuButton: false,
      suppressHeaderContextMenu: false,
      headerComponent: HeaderWithMenu,
      tooltipValueGetter: (params) => {
        const value = params.valueFormatted ?? params.value;
        if (value === null || value === undefined || value === '') return '';
        return typeof value === 'object' ? '' : String(value);
      },
      ...defaultColDefProp,
    }),
    [defaultColDefProp],
  );

  const page = paginationModel?.page ?? 0;
  const pageSize = paginationModel?.pageSize ?? 10;
  const isServerPagination = paginationMode === 'server';
  const total = isServerPagination ? Number(rowCount) || 0 : rowData?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(page, Math.max(pageCount - 1, 0));
  const rangeStart = total === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = total === 0 ? 0 : Math.min((safePage + 1) * pageSize, total);

  const displayRows = useMemo(() => {
    if (!pagination || isServerPagination) return rowData || [];
    const start = safePage * pageSize;
    return (rowData || []).slice(start, start + pageSize);
  }, [pagination, isServerPagination, rowData, safePage, pageSize]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <Box
          className={className}
          sx={{
            height,
            width: '100%',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid var(--color-border-soft)',
            '& .ag-header': {
              borderBottom: '1px solid var(--color-border)',
            },
            '& .ag-header-cell': {
              borderRight: '1px solid var(--color-border-soft)',
            },
            '& .ag-header-cell:last-of-type': {
              borderRight: 'none',
            },
            '& .ag-header-cell-text': {
              fontWeight: 700,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--color-heading)',
            },
            '& .ag-header-cell-label': {
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '6px',
            },
            '& .commonAgHeaderRoot': {
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '6px',
            },
            '& .commonAgHeaderTitle': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              cursor: 'pointer',
              flex: 1,
            },
            '& .commonAgHeaderMenuBtn': {
              border: 'none',
              background: 'transparent',
              padding: 0,
              margin: 0,
              lineHeight: 1,
              fontSize: '14px',
              fontWeight: 700,
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-heading)',
              width: 18,
              height: 18,
              borderRadius: '4px',
              cursor: 'pointer',
            },
            '& .commonAgHeaderMenuBtn:hover': {
              backgroundColor: 'var(--color-bg-soft)',
            },
            '& .ag-cell': {
              fontSize: '0.8125rem',
              lineHeight: 1.35,
              borderRight: '1px solid var(--color-border-soft)',
              borderBottom: '1px solid var(--color-border-soft)',
              userSelect: 'text',
              WebkitUserSelect: 'text',
            },
            '& .ag-cell-value, & .ag-group-value': {
              userSelect: 'text',
              WebkitUserSelect: 'text',
            },

            '& .ag-cell:last-child': {
              borderRight: 'none',
            },
            '& .ag-row-odd': {
              backgroundColor: 'var(--color-bg-soft)',
            },
          }}
        >
          <AgGridReact
            rowData={displayRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            context={context}
            rowHeight={36}
            headerHeight={36}
            animateRows
            enableBrowserTooltips
            enableCellTextSelection
            ensureDomOrder
            columnMenu="legacy"
            domLayout="normal"
            {...gridProps}
          />
        </Box>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.72)',
              zIndex: 2,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={32} sx={{ color: 'var(--color-primary)' }} />
          </Box>
        )}
      </Box>

      {pagination && onPaginationModelChange && paginationModel && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            py: 0.75,
            px: 0.25,
          }}
        >
          <FormControl size="small" variant="outlined" sx={{ minWidth: 76 }}>
            <Select
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value);
                onPaginationModelChange({ page: 0, pageSize: next });
              }}
              sx={{
                '& .MuiSelect-select': {
                  py: 0.45,
                  minHeight: 'unset',
                  fontSize: '0.76rem',
                  fontWeight: 600,
                },
                '&.MuiInputBase-root': {
                  height: 30,
                  borderRadius: '8px',
                },
              }}
            >
              {pageSizeOptions.map((n) => (
                <MenuItem key={n} value={n}>
                  {n} / page
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Pagination
            count={pageCount}
            page={safePage + 1}
            onChange={(_, value) => onPaginationModelChange({ page: value - 1, pageSize })}
            size="small"
            color="primary"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '0.76rem',
                minWidth: 28,
                height: 28,
                borderRadius: '8px',
              },
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: 'var(--color-primary)',
                color: '#fff',
                fontWeight: 700,
              },
              '& .MuiPaginationItem-root.Mui-selected:hover': {
                backgroundColor: 'var(--color-primary-dark, var(--color-primary))',
              },
            }}
          />

          <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>
            {total === 0 ? '0 rows' : `${rangeStart}–${rangeEnd} of ${total}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
